/**
 * Backend-Reiter „Events": Pflege der versteckten Seite /events.
 *
 * Genau eine Veranstaltung ist aktiv und wird öffentlich angezeigt. Texte und
 * Bilder kommen über den Markdown-Editor (Bild-Upload inklusive). Darunter die
 * Teilnehmerliste mit belegten Plätzen, Absage und CSV-Export.
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Download, ExternalLink, Loader2, Mail, Plus, Save, X } from "lucide-react";
import { toast } from "sonner";
import { MarkdownEditor } from "@/components/MarkdownEditor.lazy";
import {
  activateAdminEvent,
  archiveAdminEvent,
  cancelAdminEventRegistration,
  getAdminEventRegistrations,
  getAdminEvents,
  saveAdminEvent,
  sendAdminEventMessage,
} from "@/lib/events.functions";
import {
  EVENTS_PATH,
  formatEventDate,
  type AdminEvent,
  type AdminRegistration,
  type EventSaveInput,
} from "@/lib/events";

function defaultMessage(event: AdminEvent): { subject: string; bodyMd: string } {
  return {
    subject: `Wir freuen uns auf Sie: ${event.title}`,
    bodyMd: `wir freuen uns sehr, Sie bei „${event.title}“ begrüßen zu dürfen.\n\nBitte seien Sie spätestens um 19:30 Uhr vor Ort, damit wir pünktlich mit unseren Gästen beginnen können.`,
  };
}

function EventMessageComposer({
  event,
  recipientCount,
  onClose,
  onSent,
}: {
  event: AdminEvent;
  recipientCount: number;
  onClose: () => void;
  onSent: () => void;
}) {
  const sendFn = useServerFn(sendAdminEventMessage);
  const initial = defaultMessage(event);
  const [subject, setSubject] = useState(initial.subject);
  const [bodyMd, setBodyMd] = useState(initial.bodyMd);
  const send = useMutation({
    mutationFn: () => sendFn({ data: { eventId: event.id, subject, bodyMd } }),
    onSuccess: (r) => {
      if (r.failed > 0) toast.error(`${r.sent} gesendet, ${r.failed} fehlgeschlagen.`);
      else toast.success(`Nachricht an ${r.sent} Gäste gesendet.`);
      onSent();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <form
      className="space-y-3 rounded-md border border-border bg-muted/20 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (send.isPending) return;
        if (confirm(`Nachricht jetzt an ${recipientCount} angemeldete Gäste senden?`)) send.mutate();
      }}
    >
      <p className="font-body text-sm text-muted-foreground">
        Jeder Gast erhält die Nachricht einzeln mit persönlicher Anrede („Guten Tag …“), Termin, Ort und
        Absagelink. Abgesagte Anmeldungen erhalten nichts.
      </p>
      <label className={labelClass}>
        Betreff
        <input className={inputClass} value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} />
      </label>
      <label className={labelClass}>
        Text
        <textarea
          className={`${inputClass} min-h-40`}
          value={bodyMd}
          maxLength={5000}
          onChange={(e) => setBodyMd(e.target.value)}
        />
      </label>
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={buttonPrimary} disabled={send.isPending || subject.trim().length < 3 || bodyMd.trim().length < 10}>
          {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Mail className="h-4 w-4" aria-hidden />}
          An {recipientCount} Gäste senden
        </button>
        <button type="button" className={buttonGhost} onClick={onClose} disabled={send.isPending}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

export const Route = createFileRoute("/_admin/admin/events")({
  component: AdminEventsPage,
});

const inputClass =
  "w-full rounded-sm border border-foreground/20 bg-background px-3 py-2 font-body text-sm text-foreground focus:border-primary focus:outline-none";
const labelClass = "block font-display text-sm font-extrabold text-foreground";
const buttonPrimary =
  "inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground hover:opacity-90 disabled:opacity-60";
const buttonGhost =
  "inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 font-display text-xs font-extrabold text-foreground hover:bg-muted disabled:opacity-60";

type Draft = EventSaveInput;

const emptyDraft = (): Draft => ({
  title: "",
  startsAt: new Date(Date.now() + 14 * 86_400_000).toISOString(),
  location: "creategy, Danziger Straße 6, 10435 Berlin",
  contentMd: "",
  capacity: 30,
  registrationOpen: false,
  closedMessage: "Die Anmeldung ist derzeit geschlossen.",
});

function draftFromEvent(event: AdminEvent): Draft {
  return {
    id: event.id,
    title: event.title,
    startsAt: event.startsAt,
    location: event.location,
    contentMd: event.contentMd,
    capacity: event.capacity,
    registrationOpen: event.registrationOpenSetting,
    closedMessage: event.closedMessage,
  };
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

function AdminEventsPage() {
  const listFn = useServerFn(getAdminEvents);
  const saveFn = useServerFn(saveAdminEvent);
  const activateFn = useServerFn(activateAdminEvent);
  const archiveFn = useServerFn(archiveAdminEvent);
  const queryClient = useQueryClient();

  const {
    data: events,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin-events"],
    queryFn: () => listFn(),
  });

  const [selectedId, setSelectedId] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    if (!events) return;
    if (selectedId === null) {
      const active = events.find((e) => e.isActive) ?? events[0];
      setSelectedId(active ? active.id : "new");
    }
  }, [events, selectedId]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-events"] });

  const save = useMutation({
    mutationFn: (values: Draft) => saveFn({ data: values }),
    onSuccess: async ({ id }) => {
      toast.success("Veranstaltung gespeichert.");
      await refresh();
      setSelectedId(id);
      setDraft(null);
    },
    onError: (e: Error) => toast.error(e.message || "Speichern fehlgeschlagen."),
  });
  const activate = useMutation({
    mutationFn: (id: string) => activateFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Veranstaltung ist jetzt auf /events sichtbar.");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const archive = useMutation({
    mutationFn: (id: string) => archiveFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Veranstaltung archiviert.");
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 font-body text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Wird geladen …
      </div>
    );
  }
  if (isError) {
    return (
      <p className="p-8 font-body text-sm text-destructive">
        {(error as Error)?.message ?? "Veranstaltungen konnten nicht geladen werden."}
      </p>
    );
  }

  const selected = events?.find((e) => e.id === selectedId) ?? null;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    if (!draft.title.trim()) return toast.error("Bitte einen Titel angeben.");
    if (!draft.startsAt) return toast.error("Bitte Datum und Uhrzeit angeben.");
    save.mutate(draft);
  };

  return (
    <div>
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">Events</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Die Seite {EVENTS_PATH} ist nur per Link erreichbar und zeigt genau die aktive
            Veranstaltung.
          </p>
          <a
            href={EVENTS_PATH}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-2 font-body text-sm text-primary underline-offset-4 hover:underline"
          >
            Seite ansehen <ExternalLink className="h-4 w-4" aria-hidden />
          </a>
        </div>
        <button
          type="button"
          className={buttonPrimary}
          onClick={() => {
            setSelectedId("new");
            setDraft(emptyDraft());
          }}
        >
          <Plus className="h-4 w-4" aria-hidden /> Neu
        </button>
      </header>

      {events && events.length > 0 && (
        <ul className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-background">
          {events.map((ev) => (
            <li
              key={ev.id}
              className={`flex flex-wrap items-start gap-4 p-4 ${
                ev.id === selectedId ? "bg-secondary/40" : ""
              }`}
            >
              <button
                type="button"
                onClick={() => setSelectedId(ev.id)}
                className="min-w-0 flex-1 text-left font-body text-sm"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display font-extrabold text-foreground">{ev.title}</span>
                  {ev.isActive && (
                    <span className="rounded bg-brand-wahlen/20 px-2 py-0.5 font-display text-xs font-extrabold text-foreground">
                      aktiv
                    </span>
                  )}
                  {ev.archivedAt && (
                    <span className="text-xs text-muted-foreground">archiviert</span>
                  )}
                </div>
                <div className="mt-1 text-muted-foreground">{formatEventDate(ev.startsAt)}</div>
                <div className="mt-1 text-muted-foreground">
                  {ev.takenSeats} {ev.capacity !== null ? `/ ${ev.capacity}` : ""} Plätze belegt ·{" "}
                  {ev.registeredCount} Anmeldungen
                </div>
              </button>
              <button
                type="button"
                className={buttonGhost}
                onClick={() => {
                  setSelectedId(ev.id);
                  setDraft(draftFromEvent(ev));
                }}
              >
                Bearbeiten
              </button>
            </li>
          ))}
        </ul>
      )}

      {draft && (
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 md:p-10">
          <form
            onSubmit={onSubmit}
            className="w-full max-w-3xl rounded-lg border border-border bg-background p-6 shadow-xl"
          >
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-display text-xl font-extrabold">
                {selectedId === "new" ? "Neue Veranstaltung" : "Veranstaltung bearbeiten"}
              </h2>
              <button
                type="button"
                onClick={() => setDraft(null)}
                aria-label="Schließen"
                className="rounded p-1 text-muted-foreground hover:bg-secondary"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="ev-title">
                    Titel
                  </label>
                  <input
                    id="ev-title"
                    className={`${inputClass} mt-2`}
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="ev-start">
                    Beginn
                  </label>
                  <input
                    id="ev-start"
                    type="datetime-local"
                    className={`${inputClass} mt-2`}
                    value={toLocalInput(draft.startsAt)}
                    onChange={(e) =>
                      setDraft({ ...draft, startsAt: fromLocalInput(e.target.value) })
                    }
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="ev-location">
                    Ort
                  </label>
                  <input
                    id="ev-location"
                    className={`${inputClass} mt-2`}
                    value={draft.location}
                    onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="ev-capacity">
                    Platzobergrenze (leer = unbegrenzt; Begleitung zählt als zweiter Platz)
                  </label>
                  <input
                    id="ev-capacity"
                    type="number"
                    min={1}
                    className={`${inputClass} mt-2`}
                    value={draft.capacity ?? ""}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        capacity: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <span className={labelClass}>Inhalt (Text und Bilder)</span>
                <div className="mt-2 min-w-0 overflow-hidden">
                  <MarkdownEditor
                    value={draft.contentMd}
                    onChange={(v) => setDraft({ ...draft, contentMd: v })}
                    rows={14}
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 font-body text-sm">
                <input
                  type="checkbox"
                  checked={draft.registrationOpen}
                  onChange={(e) => setDraft({ ...draft, registrationOpen: e.target.checked })}
                />
                Anmeldung geöffnet
              </label>
              <div>
                <label className={labelClass} htmlFor="ev-closed">
                  Hinweis bei geschlossener Anmeldung
                </label>
                <input
                  id="ev-closed"
                  className={`${inputClass} mt-2`}
                  value={draft.closedMessage}
                  onChange={(e) => setDraft({ ...draft, closedMessage: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              {selected && !selected.isActive && !selected.archivedAt && (
                <button
                  type="button"
                  className={buttonGhost}
                  onClick={() => activate.mutate(selected.id)}
                  disabled={activate.isPending}
                >
                  Auf /events aktiv schalten
                </button>
              )}
              {selected && !selected.archivedAt && (
                <button
                  type="button"
                  className={buttonGhost}
                  onClick={() => {
                    if (
                      confirm(
                        "Veranstaltung archivieren? Sie verschwindet von /events, die Teilnehmerliste bleibt erhalten.",
                      )
                    )
                      archive.mutate(selected.id);
                  }}
                  disabled={archive.isPending}
                >
                  Archivieren
                </button>
              )}
              <button type="button" className={buttonGhost} onClick={() => setDraft(null)}>
                Abbrechen
              </button>
              <button type="submit" className={buttonPrimary} disabled={save.isPending}>
                {save.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Save className="h-4 w-4" aria-hidden />
                )}
                {save.isPending ? "Speichert …" : "Speichern"}
              </button>
            </div>
          </form>
        </div>
      )}

      {selected && (
        <div className="mt-8">
          <RegistrationList event={selected} onChanged={refresh} />
        </div>
      )}
    </div>
  );
}

function RegistrationList({ event, onChanged }: { event: AdminEvent; onChanged: () => void }) {
  const listFn = useServerFn(getAdminEventRegistrations);
  const cancelFn = useServerFn(cancelAdminEventRegistration);
  const queryClient = useQueryClient();
  const [composeOpen, setComposeOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-event-registrations", event.id],
    queryFn: () => listFn({ data: { id: event.id } }),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => cancelFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Teilnahme abgesagt.");
      queryClient.invalidateQueries({ queryKey: ["admin-event-registrations", event.id] });
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function exportCsv(rows: AdminRegistration[]) {
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const lines = [
      ["Vorname", "Nachname", "E-Mail", "Telefon", "Begleitung", "Status", "Angemeldet am"].join(
        ";",
      ),
      ...rows.map((r) =>
        [
          r.firstName,
          r.lastName,
          r.email,
          r.phone,
          r.plusOne ? "ja" : "nein",
          r.status === "registered" ? "angemeldet" : "abgesagt",
          new Date(r.createdAt).toLocaleString("de-DE"),
        ]
          .map(esc)
          .join(";"),
      ),
    ];
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `teilnehmende-${event.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const rows = data ?? [];
  const activeCount = rows.filter((r) => r.status === "registered").length;
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-extrabold text-primary">
          Teilnehmende · {event.takenSeats}
          {event.capacity !== null ? ` / ${event.capacity}` : ""} Plätze belegt
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={buttonGhost}
            onClick={() => setComposeOpen((v) => !v)}
            disabled={activeCount === 0}
          >
            <Mail className="h-4 w-4" aria-hidden /> Nachricht an Teilnehmende
          </button>
          <button
            type="button"
            className={buttonGhost}
            onClick={() => exportCsv(rows)}
            disabled={rows.length === 0}
          >
            <Download className="h-4 w-4" aria-hidden /> CSV exportieren
          </button>
        </div>
      </div>
      {composeOpen && (
        <EventMessageComposer
          event={event}
          recipientCount={activeCount}
          onClose={() => setComposeOpen(false)}
          onSent={() => {
            setComposeOpen(false);
            queryClient.invalidateQueries({ queryKey: ["admin-event-registrations", event.id] });
          }}
        />
      )}
      {isLoading ? (
        <p className="font-body text-sm text-muted-foreground">Wird geladen …</p>
      ) : rows.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground">Noch keine Anmeldungen.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full font-body text-sm">
            <thead className="bg-muted/40 text-left font-display text-xs font-extrabold">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">E-Mail</th>
                <th className="p-3">Telefon</th>
                <th className="p-3">+1</th>
                <th className="p-3">Status</th>
                <th className="p-3">Mails</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3">
                    {r.firstName} {r.lastName}
                  </td>
                  <td className="p-3">{r.email}</td>
                  <td className="p-3">{r.phone}</td>
                  <td className="p-3">{r.plusOne ? "ja" : "–"}</td>
                  <td className="p-3">
                    {r.status === "registered" ? (
                      "angemeldet"
                    ) : (
                      <span className="text-muted-foreground">
                        abgesagt ({r.cancelledBy === "team" ? "Team" : "selbst"})
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {r.confirmationSentAt ? "Bestätigung" : "–"}
                    {r.reminder7SentAt ? " · 7 Tage" : ""}
                    {r.reminder1SentAt ? " · 1 Tag" : ""}
                    {r.lastMessageSentAt
                      ? ` · Nachricht ${new Date(r.lastMessageSentAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}`
                      : ""}
                  </td>
                  <td className="p-3 text-right">
                    {r.status === "registered" && (
                      <button
                        type="button"
                        className={buttonGhost}
                        onClick={() => {
                          if (confirm(`Teilnahme von ${r.firstName} ${r.lastName} absagen?`))
                            cancel.mutate(r.id);
                        }}
                      >
                        Absagen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
